import APIConfig from "../config/apiConfig";
import APIService from "service/network/APIService";
import Helper from "util/Helper";

/**
 * Progress notes. Ported from bridge's PatientProgressNotesService — same Eve
 * (Mongo-REST) API, same query shapes. See APIConfig.progressNotes for what the
 * `where`/`embedded`/`max_results`/`page` slots mean and why the {n} order looks
 * scrambled.
 *
 * The list responds `{ items: [...], meta: { page, max_results, total } }`.
 * Bridge only reads `items`; `meta.total` is real and lets the feed report a
 * true count instead of a hardcoded one.
 *
 * Requires a who="employee" session — the doctor JWT is rejected by these ops
 * endpoints. See memory: phr-login-is-employee-flow.
 */
class PatientProgressNotesService {
  constructor() {
    this.apiService = new APIService();
    // NOT dead code. Every URL below is built with String.prototype.format, and
    // that method does not exist until a Helper is constructed — its ctor runs
    // _initializeNaturalExtensions(), which installs it. Today the import of
    // APIService happens to pull in newAPIService, whose module-level instance
    // builds a Helper, so format is already there; this makes the dependency
    // explicit instead of resting on that chain. Bridge's service does the same.
    this.helper = new Helper();
  }

  /** Every note for a patient, newest first. `page` is 1-based. */
  get(patientId, page, maxResults, onResponse, onError) {
    const url = APIConfig.progressNotes.getAll.format(
      patientId,
      maxResults,
      page,
      new Date().getTime()
    );
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * A single note with its comments inlined. Used to refresh one card in place
   * after a write, so acting on a note deep in the feed doesn't collapse the
   * list back to page 1.
   */
  getOne(noteId, onResponse, onError) {
    const url = APIConfig.progressNotes.getOne.format(noteId, new Date().getTime());
    return this.apiService.get(url, onResponse, onError);
  }

  /** As get(), narrowed to one tag (the int from progressNoteTags). */
  getWithType(patientId, page, maxResults, tag, onResponse, onError) {
    const url = APIConfig.progressNotes.getAllWithType.format(
      patientId,
      maxResults,
      page,
      tag,
      new Date().getTime()
    );
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Body regex search. NOTE the endpoint ignores tag AND paging — it only
   * substitutes patientId and the text — so callers must reset the active tag to
   * ALL when searching, or the chips will contradict the results. That is what
   * bridge does.
   */
  search(patientId, searchText, onResponse, onError) {
    const url = APIConfig.progressNotes.search.format(
      patientId,
      0,
      0,
      0,
      encodeURIComponent(searchText)
    );
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Create. Body shape (bridge postRequest):
   *   { patientId:int, body, author:{name, employeeId}, tag:int,
   *     acknowledgers: [], comments: [] }
   */
  createProgressNote(progressNote, onResponse, onError) {
    return this.apiService.post(
      APIConfig.progressNotes.new,
      progressNote,
      onResponse,
      onError
    );
  }

  updateProgressNote(progressNoteId, progressNote, onResponse, onError) {
    const url = APIConfig.progressNotes.update.format(progressNoteId);
    return this.apiService.patch(url, progressNote, onResponse, onError);
  }

  createComment(comment, onResponse, onError) {
    return this.apiService.post(
      APIConfig.progressNoteComment.new,
      comment,
      onResponse,
      onError
    );
  }

  updateComment(commentId, comment, onResponse, onError) {
    const url = APIConfig.progressNoteComment.update.format(commentId);
    return this.apiService.patch(url, comment, onResponse, onError);
  }

  /* ---------- Zoho tasks (prod-only — see APIConfig.progressNoteTask) ---------- */

  getProjects(onResponse, onError) {
    return this.apiService.get(APIConfig.progressNoteTask.projects, onResponse, onError);
  }

  getUsersByProject(projectId, onResponse, onError) {
    const url = APIConfig.progressNoteTask.usersByProject.format(projectId);
    return this.apiService.get(url, onResponse, onError);
  }

  newTask(task, onResponse, onError) {
    return this.apiService.post(
      APIConfig.progressNoteTask.newTask,
      task,
      onResponse,
      onError
    );
  }
}

export default PatientProgressNotesService;
